import { Injectable, OnDestroy } from '@angular/core';
import { Router, UrlSegment, UrlSegmentGroup } from '@angular/router';
import { resetStores } from '@datorama/akita';
import { Observable } from 'rxjs';
import { BusinessRole } from '../../../../../../api/src/business/business-user.entity';
import { SessionQuery } from '../session';
import { BusinessSwitchState } from './business-switch-state';
import { Business } from './business.model';
import { BusinessQuery } from './business.query';
import { BusinessStore } from './business.store';

/**
 * Akita store names that survive a business switch — user/session/global state,
 * not business-scoped data. `resetStores({ exclude })` resets every other store
 * to its initial state, so a newly-added store is reset by default (fail-safe);
 * only genuinely global stores are listed here. Enforced by the accompanying
 * spec.
 */
export const BUSINESS_SWITCH_EXEMPT_STORES = ['business', 'global', 'session', 'user', 'language', 'music', 'news'];

/**
 * Prefix for the per-user localStorage key that persists the active business
 * selection. Scoped by user id so a shared browser never leaks one user's
 * selection to the next (origin R6).
 */
const ACTIVE_BUSINESS_KEY_PREFIX = 'VYC_ACTIVE_BUSINESS_';

/**
 * A dirty-editor probe. U3/U5 register the real check (e.g. a CanDeactivate /
 * dirty-form signal); until then the default reports "not dirty" so cross-tab
 * follows apply immediately.
 */
export type DirtyEditorProbe = () => boolean;

/**
 * BusinessContextService — the single authority for the active business.
 *
 * Owns selection, per-user persistence, load-time resolution (invoked from an
 * APP_INITIALIZER before the router starts), the navigate-free
 * `setActiveBusinessContext` primitive, and cross-tab follower sync. The
 * router-driven switch flow (`switchBusiness`) and the store-reset registry are
 * layered on in U3; the deep-link guard consumes `setActiveBusinessContext` in
 * U5.
 */
@Injectable({ providedIn: 'root' })
export class BusinessContextService implements OnDestroy {
	/** Stores to reset on a business switch. Populated by U3's registry. */
	private readonly storeResetHooks: Array<() => void> = [];

	/** Probe used to decide whether a cross-tab follow should defer. */
	private dirtyEditorProbe: DirtyEditorProbe = () => false;

	/** Deferred follower target, applied once the editor is no longer dirty. */
	private deferredFollowerBusinessId: string | null = null;

	/**
	 * The user whose selection the store's active id belongs to. The store is
	 * tab-global and unpersisted while the key is per user, so without this a
	 * second user signing in without a logout inherits the previous selection.
	 */
	private activeSelectionUserId: string | null = null;

	/** Bound handler kept so it can be detached on destroy (see ngOnDestroy). */
	private readonly onStorage = (event: StorageEvent): void => this.handleCrossTabChange(event);

	constructor(
		private readonly businessStore: BusinessStore,
		private readonly businessQuery: BusinessQuery,
		private readonly sessionQuery: SessionQuery,
		private readonly router: Router,
		private readonly switchState: BusinessSwitchState
	) {
		this.listenForCrossTabChanges();
	}

	/**
	 * User-initiated switch (from the account-bar switcher or the settings
	 * sidebar). Navigates so the existing CanLeavePageGuard fires for dirty
	 * editors, then re-scopes and re-fetches the destination. Detail routes fall
	 * back to their parent list (via `data.businessSwitchFallback`); routes keyed
	 * on a business id in the URL get that segment rewritten (via
	 * `data.businessIdParam`); list routes reload in place.
	 */
	public switchBusiness(businessId: string): void {
		const previousId = this.getActiveBusinessId();
		if (businessId === previousId) {
			return;
		}
		const target = this.resolveSwitchTarget(businessId);
		this.switchState.begin();
		// Scope to the new business first so the reloaded destination fetches
		// against the new context; revert if the leave-page guard vetoes.
		this.setActiveBusinessContext(businessId);
		this.router.navigateByUrl(target).then(
			navigated => {
				if (!navigated && previousId) {
					this.setActiveBusinessContext(previousId);
				}
				this.switchState.end();
			},
			() => {
				if (previousId) {
					this.setActiveBusinessContext(previousId);
				}
				this.switchState.end();
			}
		);
	}

	/**
	 * The URL to navigate to on a switch, first match wins: `businessSwitchFallback`
	 * for a detail route, else the current URL with its business-id segment
	 * rewritten (`businessIdParam` — without it the route re-activates for the old
	 * business and its resolver reverts the switch), else the URL unchanged.
	 */
	private resolveSwitchTarget(businessId: string): string {
		let route = this.router.routerState.snapshot.root;
		let fallback: string | undefined;
		let staleBusinessId: string | undefined;
		while (route.firstChild) {
			route = route.firstChild;
			if (route.data && route.data['businessSwitchFallback']) {
				fallback = route.data['businessSwitchFallback'] as string;
			}
			// `data` and `params` are inherited from the componentless
			// `business/:businessId` parent, so the leaf carries both.
			const paramName = route.data?.['businessIdParam'] as string | undefined;
			if (paramName && route.params?.[paramName]) {
				staleBusinessId = route.params[paramName] as string;
			}
		}

		if (fallback) {
			return fallback;
		}
		if (staleBusinessId && staleBusinessId !== businessId) {
			return this.replaceBusinessIdSegment(this.router.url, staleBusinessId, businessId);
		}
		return this.router.url;
	}

	/**
	 * Swap the business-id segment, keeping sub-page, matrix/query params and
	 * fragment intact. First match only, so a repeated id elsewhere is left alone.
	 */
	private replaceBusinessIdSegment(url: string, staleId: string, businessId: string): string {
		const tree = this.router.parseUrl(url);
		const replaceInGroup = (group: UrlSegmentGroup): boolean => {
			const index = group.segments.findIndex(segment => segment.path === staleId);
			if (index > -1) {
				group.segments[index] = new UrlSegment(businessId, group.segments[index].parameters);
				return true;
			}
			return Object.values(group.children).some(child => replaceInGroup(child));
		};
		replaceInGroup(tree.root);
		return tree.toString();
	}

	/** The active business entity, or undefined when none is resolved. */
	public get activeBusiness$(): Observable<Business> {
		return this.businessQuery.selectActive() as Observable<Business>;
	}

	/** The active business id, or undefined when none is resolved. */
	public get activeBusinessId$(): Observable<string> {
		return this.businessQuery.selectActiveId() as Observable<string>;
	}

	public getActiveBusinessId(): string | undefined {
		return this.businessQuery.getActiveId() as string | undefined;
	}

	public getActiveBusiness(): Business | undefined {
		return this.businessQuery.getActive() as Business | undefined;
	}

	/**
	 * The user's role in the active business, for permission-conditional UI.
	 * Super-admins viewing a business they are not a member of get a defined
	 * admin-level role rather than undefined (mirrors the API bypass).
	 */
	public getActiveRole(): BusinessRole | undefined {
		const active = this.getActiveBusiness();
		if (active?.role) {
			return active.role;
		}
		if (this.sessionQuery.isAtLeastSuperAdmin()) {
			return BusinessRole.Administrator;
		}
		return undefined;
	}

	/**
	 * Bootstrap entry point, invoked from the APP_INITIALIZER before the router
	 * starts. Hydrates the business store from the synchronously-rehydrated
	 * session profile (the store is otherwise empty until `getUserStatus`
	 * returns), then resolves the active business. This guarantees an active
	 * business is set before any business-scoped page fetches on a warm load.
	 */
	public initializeActiveBusiness(): void {
		const businesses = this.sessionQuery.getProfile()?.businesses ?? [];
		if (businesses.length && !this.businessQuery.getCount()) {
			this.businessStore.set(businesses);
		}
		this.resolveActiveBusiness();
	}

	/**
	 * Resolve the active business from the currently-hydrated businesses.
	 * Re-run whenever the profile's business list is refreshed (e.g. after login
	 * or `getUserStatus`). Keeps an already-resolved selection when it is still
	 * accessible *and* belongs to the signed-in user; else prefers that user's
	 * persisted selection; else falls back to the first eligible (else first)
	 * business. Idempotent.
	 */
	public resolveActiveBusiness(): void {
		const businesses = this.getBusinesses();
		if (!businesses.length) {
			this.businessStore.setActive(null);
			// Attribute even an empty resolve, so a later BusinessService.setActive
			// isn't mistaken for another user's.
			this.markActiveSelectionOwner();
			return;
		}

		// A live selection wins, so a mid-session refresh can't snap the tab back
		// where active diverges from persisted (a deferred cross-tab follow, or
		// BusinessService.setActive, which doesn't persist). Within one user's
		// session only — see `activeSelectionUserId`.
		const activeId = this.getActiveBusinessId();
		const userId = this.currentUserId();
		// With no user the persisted key is unreadable anyway, so keep the selection.
		const ownedByCurrentUser = !userId || this.activeSelectionUserId === userId;
		if (activeId && ownedByCurrentUser && businesses.some(b => b.id === activeId)) {
			return;
		}

		// A deferred target belongs to the previous user; applying it would persist
		// their business under the new user's key.
		if (activeId && !ownedByCurrentUser) {
			this.deferredFollowerBusinessId = null;
		}

		const persistedId = this.readPersistedBusinessId();
		if (persistedId && businesses.some(b => b.id === persistedId)) {
			this.businessStore.setActive(persistedId);
			this.markActiveSelectionOwner();
			return;
		}

		const fallback = this.pickDefaultBusiness(businesses);
		this.businessStore.setActive(fallback.id);
		this.markActiveSelectionOwner();
		this.writePersistedBusinessId(fallback.id);
	}

	/**
	 * Navigate-free context switch: persist the selection, mark active, and
	 * reset business-scoped stores. Used by the router-driven `switchBusiness`
	 * (U3), the deep-link guard (U5, where the requested URL wins so no
	 * navigation happens here), and the clean cross-tab follower path.
	 */
	public setActiveBusinessContext(businessId: string): void {
		this.businessStore.setActive(businessId);
		this.markActiveSelectionOwner();
		this.writePersistedBusinessId(businessId);
		this.resetBusinessScopedStores();
	}

	/** U3 registers each business-scoped store's reset here. */
	public registerStoreReset(reset: () => void): void {
		this.storeResetHooks.push(reset);
	}

	/** U3/U5 register the real dirty-editor probe for cross-tab defer. */
	public registerDirtyEditorProbe(probe: DirtyEditorProbe): void {
		this.dirtyEditorProbe = probe;
	}

	/**
	 * Apply a follower tab's deferred switch once its editor is no longer dirty.
	 * Called by the editor teardown / save path.
	 */
	public applyDeferredFollowerSwitch(): void {
		if (this.deferredFollowerBusinessId) {
			const target = this.deferredFollowerBusinessId;
			this.deferredFollowerBusinessId = null;
			this.setActiveBusinessContext(target);
		}
	}

	public hasDeferredFollowerSwitch(): boolean {
		return this.deferredFollowerBusinessId !== null;
	}

	/** Clear the persisted selection on logout (paired with store teardown). */
	public clearPersistedContext(): void {
		const key = this.activeBusinessKey();
		if (key) {
			localStorage.removeItem(key);
		}
		this.deferredFollowerBusinessId = null;
		this.activeSelectionUserId = null;
		// Logout is a stronger boundary than a business switch, so everything a
		// switch clears has to go here too. Login does not reload the page, so a
		// store left populated is read by the next user in the same tab.
		this.resetBusinessScopedStores();
	}

	private resetBusinessScopedStores(): void {
		// Reset every store except the global/user ones. Fail-safe: a new store
		// is reset by default unless explicitly exempted above.
		resetStores({ exclude: BUSINESS_SWITCH_EXEMPT_STORES });
		// Extra teardown that isn't a store reset (e.g. encode-job SSE polling).
		this.storeResetHooks.forEach(reset => reset());
	}

	private getBusinesses(): Business[] {
		return (this.businessQuery.getAll() as Business[]) ?? [];
	}

	private pickDefaultBusiness(businesses: Business[]): Business {
		const eligible = businesses.find(b => !b.delinquent && b.hasActiveSubscription !== false);
		return eligible ?? businesses[0];
	}

	private currentUserId(): string | null {
		return this.sessionQuery.getProfile()?.id ?? null;
	}

	/** Attribute the store's active id to the signed-in user (see the field). */
	private markActiveSelectionOwner(): void {
		this.activeSelectionUserId = this.currentUserId();
	}

	private activeBusinessKey(): string | null {
		const userId = this.currentUserId();
		return userId ? `${ACTIVE_BUSINESS_KEY_PREFIX}${userId}` : null;
	}

	private readPersistedBusinessId(): string | null {
		const key = this.activeBusinessKey();
		return key ? localStorage.getItem(key) : null;
	}

	private writePersistedBusinessId(businessId: string): void {
		const key = this.activeBusinessKey();
		if (key) {
			localStorage.setItem(key, businessId);
		}
	}

	/**
	 * Follow business switches made in other tabs (R19). A clean tab applies
	 * immediately via the navigate-free primitive; a tab with a dirty editor
	 * defers until the editor is saved or closed. Follower tabs never write the
	 * key back, so tabs cannot ping-pong or diverge.
	 */
	private listenForCrossTabChanges(): void {
		window.addEventListener('storage', this.onStorage);
	}

	/** Detach the cross-tab listener so instances don't outlive their scope. */
	public ngOnDestroy(): void {
		window.removeEventListener('storage', this.onStorage);
	}

	private handleCrossTabChange(event: StorageEvent): void {
		const key = this.activeBusinessKey();
		if (!key || event.key !== key || !event.newValue) {
			return;
		}
		if (event.newValue === this.getActiveBusinessId()) {
			return;
		}
		if (this.dirtyEditorProbe()) {
			this.deferredFollowerBusinessId = event.newValue;
			return;
		}
		this.followWithoutPersisting(event.newValue);
	}

	/** Cross-tab apply path: mark active and reset stores, but do NOT re-persist. */
	private followWithoutPersisting(businessId: string): void {
		this.businessStore.setActive(businessId);
		this.markActiveSelectionOwner();
		this.resetBusinessScopedStores();
	}
}
